/**
 * OnboardingFlow — the six-step async onboarding flow (T-032 + T-033).
 *
 * PRD-003 PR-06 (context doc with the V1 atoms), PR-52 (non-interactive
 * intermission), DATA-02 (five-section context doc), DATA-18 (onboarding
 * state), US-04. Copy follows the client-communication register (warmer,
 * attentive — design-guidelines.md §"Tone defaults per surface"): plain,
 * present-tense, no jargon. American English; no em dashes.
 *
 * Steps (content-pages.md §Onboarding pages, journeys.md §1.2):
 *   1 factual      company        (name, stage, what they sell, where)
 *   2 factual      audiences      (primary buyer, secondary, users, not-serving)
 *   3 intermission PR-52 locked copy, single CTA "I'm back", collects no data
 *   4 reflective   strategic_ctx  (one-liner, positioning, competitors, blocker)
 *   5 reflective   voice          (voice attrs, banned phrases, tone, language)
 *   6 forward      priorities     (first 30 days, deliver first, avoid first mo.)
 * Submit -> persist sections, status='submitted', notify Eric, redirect.
 *
 * Autosave: on every step transition the cumulative sections are persisted as
 * a draft (T-032 AC: "Auto-save partial state ... under draft"). The persist
 * path is `lib/onboarding.ts` which routes through the SECURITY DEFINER RPC
 * documented there (migration 0015 candidate — see that file + IMPL-NOTES).
 */

import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type {
  OnboardingSections,
  CompanySection,
  AudiencesSection,
  StrategicContextSection,
  VoiceConstraintsSection,
  InitialPrioritiesSection,
} from '@studio/api-schemas';
import { Button } from '@/components/ui/button';
import { persistOnboarding } from '@/lib/onboarding';
import { OnboardingLayout } from './OnboardingLayout';
import {
  StepHeading,
  TextField,
  LongTextField,
  ListField,
} from './fields';

const TOTAL_STEPS = 6;

interface OnboardingFlowProps {
  token: string;
  initialSections: OnboardingSections;
}

export function OnboardingFlow({ token, initialSections }: OnboardingFlowProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sections, setSections] = useState<OnboardingSections>(initialSections);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const patch = useCallback(
    <K extends keyof OnboardingSections>(
      key: K,
      value: OnboardingSections[K],
    ) => {
      setSections((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Autosave the cumulative draft, then move forward. A save failure does not
  // trap the subscriber on the step — their input stays in component state and
  // is retried on the next transition / final submit. The honest exception is
  // `migration_pending`: the RPC that persists is not deployed yet, so we say
  // so rather than imply the answers were stored.
  const goNext = useCallback(async () => {
    setBanner(null);
    setBusy(true);
    const outcome = await persistOnboarding(sections, { submit: false });
    setBusy(false);
    if (outcome.kind === 'error') {
      setBanner(
        'That did not save just now. Your answers are still here. Keep going, and it will retry.',
      );
    } else if (outcome.kind === 'migration_pending') {
      setBanner(
        'Saving is not switched on in this environment yet. You can still walk through every step; submission is held until it is.',
      );
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  }, [sections]);

  const goBack = useCallback(() => {
    setBanner(null);
    setStep((s) => Math.max(s - 1, 1));
  }, []);

  const submit = useCallback(async () => {
    setBanner(null);
    setBusy(true);
    const outcome = await persistOnboarding(sections, { submit: true });
    setBusy(false);
    if (outcome.kind === 'submitted') {
      navigate({ to: '/onboarding/$token/submitted', params: { token } });
      return;
    }
    if (outcome.kind === 'migration_pending') {
      setBanner(
        'Submission is not switched on in this environment yet. Nothing you wrote was lost.',
      );
      return;
    }
    setBanner(
      'Submitting did not go through. Nothing was lost. Try once more in a moment.',
    );
  }, [sections, navigate, token]);

  return (
    <OnboardingLayout step={step} totalSteps={TOTAL_STEPS}>
      {banner && (
        <div
          role="status"
          className="mb-6 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-linen)] px-4 py-3 text-sm text-[color:var(--color-navy)]"
        >
          {banner}
        </div>
      )}

      {step === 1 && (
        <CompanyStep
          value={sections.company ?? {}}
          onChange={(v) => patch('company', v)}
        />
      )}
      {step === 2 && (
        <AudiencesStep
          value={sections.audiences ?? {}}
          onChange={(v) => patch('audiences', v)}
        />
      )}
      {step === 3 && <IntermissionStep />}
      {step === 4 && (
        <StrategicContextStep
          value={sections.strategic_context ?? {}}
          onChange={(v) => patch('strategic_context', v)}
        />
      )}
      {step === 5 && (
        <VoiceStep
          value={sections.voice_and_constraints ?? {}}
          onChange={(v) => patch('voice_and_constraints', v)}
        />
      )}
      {step === 6 && (
        <PrioritiesStep
          value={sections.initial_priorities ?? {}}
          onChange={(v) => patch('initial_priorities', v)}
        />
      )}

      <div className="mt-10 flex items-center justify-between gap-4">
        {step > 1 ? (
          <Button
            type="button"
            variant="ghost"
            onClick={goBack}
            disabled={busy}
          >
            Back
          </Button>
        ) : (
          <span />
        )}

        {step === 3 ? (
          <Button type="button" onClick={goNext} disabled={busy} size="lg">
            I&apos;m back
          </Button>
        ) : step < TOTAL_STEPS ? (
          <Button type="button" onClick={goNext} disabled={busy} size="lg">
            {busy ? 'Saving...' : 'Continue'}
          </Button>
        ) : (
          <Button type="button" onClick={submit} disabled={busy} size="lg">
            {busy ? 'Submitting...' : 'Send this to Eric'}
          </Button>
        )}
      </div>
    </OnboardingLayout>
  );
}

// ── Step 1: Company (factual) ──────────────────────────────────────────────

function CompanyStep({
  value,
  onChange,
}: {
  value: CompanySection;
  onChange: (v: CompanySection) => void;
}) {
  const set = (k: keyof CompanySection, v: string) =>
    onChange({ ...value, [k]: v });
  return (
    <section>
      <StepHeading
        eyebrow="The basics"
        title="Tell me about the company"
        intro="Short answers are fine. This is the ground the work stands on, not an essay."
      />
      <div className="space-y-6">
        <TextField
          id="company-name"
          label="Company name"
          value={value.name ?? ''}
          onChange={(v) => set('name', v)}
        />
        <TextField
          id="company-stage"
          label="Where are you in the journey?"
          help="Pre-launch, early revenue, scaling, whatever fits."
          value={value.stage ?? ''}
          onChange={(v) => set('stage', v)}
        />
        <LongTextField
          id="company-sell"
          label="What do you sell?"
          help="In your own words. The product, not the pitch."
          value={value.what_they_sell ?? ''}
          onChange={(v) => set('what_they_sell', v)}
        />
        <TextField
          id="company-where"
          label="Where do you sell it?"
          help="Markets, regions, or channels."
          value={value.where_they_sell ?? ''}
          onChange={(v) => set('where_they_sell', v)}
        />
      </div>
    </section>
  );
}

// ── Step 2: Audiences (factual) ────────────────────────────────────────────

function AudiencesStep({
  value,
  onChange,
}: {
  value: AudiencesSection;
  onChange: (v: AudiencesSection) => void;
}) {
  return (
    <section>
      <StepHeading
        eyebrow="The basics"
        title="Who is this for?"
        intro="Be specific. Vague audiences make vague work."
      />
      <div className="space-y-6">
        <LongTextField
          id="aud-primary"
          label="Primary buyer"
          help="The person who decides to pay. Role, situation, what they care about."
          value={value.primary_buyer ?? ''}
          onChange={(v) => onChange({ ...value, primary_buyer: v })}
        />
        <ListField
          id="aud-secondary"
          label="Secondary buyers"
          help="Anyone else in the room when the decision happens. Comma-separated."
          value={value.secondary_buyers ?? []}
          onChange={(v) => onChange({ ...value, secondary_buyers: v })}
        />
        <LongTextField
          id="aud-users"
          label="Who actually uses it?"
          help="Sometimes the same as the buyer. Often not."
          value={value.users ?? ''}
          onChange={(v) => onChange({ ...value, users: v })}
        />
        <LongTextField
          id="aud-not"
          label="Who are you not serving?"
          help="The honest version. Saying no to someone sharpens the yes."
          value={value.who_not_serving ?? ''}
          onChange={(v) => onChange({ ...value, who_not_serving: v })}
        />
      </div>
    </section>
  );
}

// ── Step 3: Intermission (PR-52, non-interactive, collects no data) ─────────

function IntermissionStep() {
  // Locked copy per PR-52 / design-guidelines.md §"Onboarding intermission".
  // Larger type + significantly more whitespace (design-guidelines.md §5).
  // No fields. The single "I'm back" CTA is rendered by the flow footer and is
  // keyboard reachable + Enter/Space operable (DES-09).
  //
  // Final design pass: matches the prototype's intermission step (pixel glyph,
  // serif headline, three linen rows). Each locked line is kept verbatim; the
  // leading glyphs are decorative (aria-hidden) so they are never read as
  // meaning, satisfying the no-color-only-signal rule (DES-09 / ARCH-026).
  const ROWS: ReadonlyArray<{ glyph: string; line: string }> = [
    { glyph: '+', line: 'Go outside. Five minutes.' },
    { glyph: '~', line: 'Make something warm to drink.' },
    { glyph: '*', line: 'Then come back and be honest.' },
  ];
  return (
    <section className="py-6 text-center sm:py-10">
      <p
        className="mb-6 font-pixel text-3xl text-[color:var(--color-navy)]"
        aria-hidden="true"
      >
        ◎
      </p>
      <h2 className="mb-4 font-display text-2xl italic leading-tight text-[color:var(--color-navy)] sm:text-3xl">
        Before the next part.
      </h2>
      <p className="mx-auto mb-8 max-w-md text-[15px] leading-relaxed text-[color:var(--color-text-mute)]">
        The next questions ask for honest, unfiltered thinking, not your
        investor narrative. There are no wrong answers here.
      </p>
      <ul className="mx-auto flex max-w-sm flex-col gap-2 text-left">
        {ROWS.map((r) => (
          <li
            key={r.line}
            className="flex items-center gap-3 rounded-md bg-[color:var(--color-linen)] px-4 py-3"
          >
            <span
              className="font-pixel text-lg text-[color:var(--color-rose-dark)]"
              aria-hidden="true"
            >
              {r.glyph}
            </span>
            <span className="text-sm text-[color:var(--color-navy)]">
              {r.line}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Step 4: Strategic context (reflective) ─────────────────────────────────

function StrategicContextStep({
  value,
  onChange,
}: {
  value: StrategicContextSection;
  onChange: (v: StrategicContextSection) => void;
}) {
  return (
    <section>
      <StepHeading
        eyebrow="The honest part"
        title="Where things actually stand"
        intro="This is the part most people round up on. Try not to."
      />
      <div className="space-y-6">
        <LongTextField
          id="sc-oneliner"
          label="Your current one-liner"
          help="The sentence you use today, even if you do not love it."
          value={value.current_one_liner ?? ''}
          onChange={(v) => onChange({ ...value, current_one_liner: v })}
        />
        <LongTextField
          id="sc-positioning"
          label="How you position right now"
          help="What you say you are, and who you say it to."
          value={value.current_positioning ?? ''}
          onChange={(v) => onChange({ ...value, current_positioning: v })}
        />
        <ListField
          id="sc-competitors"
          label="Competitors of note"
          help="Who you lose to, or who comes up in the room. Comma-separated."
          value={value.competitors_of_note ?? []}
          onChange={(v) => onChange({ ...value, competitors_of_note: v })}
        />
        <LongTextField
          id="sc-working"
          label="What is working"
          help="The part you would not change."
          value={value.whats_working ?? ''}
          onChange={(v) => onChange({ ...value, whats_working: v })}
        />
        <LongTextField
          id="sc-blocker"
          label="Your biggest blocker"
          help="The thing that, if it moved, would change the most."
          value={value.biggest_blocker ?? ''}
          onChange={(v) => onChange({ ...value, biggest_blocker: v })}
        />
      </div>
    </section>
  );
}

// ── Step 5: Voice and constraints (reflective) ─────────────────────────────

function VoiceStep({
  value,
  onChange,
}: {
  value: VoiceConstraintsSection;
  onChange: (v: VoiceConstraintsSection) => void;
}) {
  return (
    <section>
      <StepHeading
        eyebrow="How you sound"
        title="Voice and the lines you do not cross"
        intro="So the work sounds like you, and never says the thing you would never say."
      />
      <div className="space-y-6">
        <ListField
          id="voice-attrs"
          label="Voice attributes"
          help="A few words for how you want to sound. Comma-separated."
          value={value.voice_attributes ?? []}
          onChange={(v) => onChange({ ...value, voice_attributes: v })}
        />
        <ListField
          id="voice-banned"
          label="Phrases to never use"
          help="Words or claims that are off-limits. Comma-separated."
          value={value.banned_phrases ?? []}
          onChange={(v) => onChange({ ...value, banned_phrases: v })}
        />
        <TextField
          id="voice-tone"
          label="Tone in one breath"
          help="Warm and plain? Sharp and technical? Your call."
          value={value.tone ?? ''}
          onChange={(v) => onChange({ ...value, tone: v })}
        />
        <TextField
          id="voice-language"
          label="Language"
          help="English, Norwegian, both? Note any per-audience split."
          value={value.language ?? ''}
          onChange={(v) => onChange({ ...value, language: v })}
        />
        <LongTextField
          id="voice-reg"
          label="Regulatory or legal constraints"
          help="Anything the work has to stay inside. Skip if none."
          value={value.regulatory_constraints ?? ''}
          onChange={(v) =>
            onChange({ ...value, regulatory_constraints: v })
          }
        />
      </div>
    </section>
  );
}

// ── Step 6: Initial priorities (forward-looking) ───────────────────────────

function PrioritiesStep({
  value,
  onChange,
}: {
  value: InitialPrioritiesSection;
  onChange: (v: InitialPrioritiesSection) => void;
}) {
  return (
    <section>
      <StepHeading
        eyebrow="What comes first"
        title="The next thirty days"
        intro="Last one. This points the first piece of work at the right thing."
      />
      <div className="space-y-6">
        <LongTextField
          id="pri-30"
          label="What matters most in the first 30 days?"
          value={value.first_30_days ?? ''}
          onChange={(v) => onChange({ ...value, first_30_days: v })}
        />
        <LongTextField
          id="pri-first"
          label="What would you want delivered first?"
          help="If you could only get one thing back, what is it?"
          value={value.what_to_deliver_first ?? ''}
          onChange={(v) =>
            onChange({ ...value, what_to_deliver_first: v })
          }
        />
        <LongTextField
          id="pri-avoid"
          label="What should we not touch in month one?"
          help="The thing that is not ready, or not yours to move yet."
          value={value.what_to_avoid_first_month ?? ''}
          onChange={(v) =>
            onChange({ ...value, what_to_avoid_first_month: v })
          }
        />
      </div>
    </section>
  );
}
